import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { FileUploader } from '../../../components/ProjectList/FileUploader';
import { useUploadArtifacts } from '../../../hooks/useDeliveryDocuments';
import { useUploadProjectFiles } from '../../../hooks/useProjectFiles';
import { useProgramContext } from '../../../layouts/ProgramLayout';
import { proposalProjectApiConnect } from '../../../lib/api/proposalProjectApi.connect';
import { useProcessingJobsStore } from '../../../lib/processing/jobsStore';
import { formatUtcYmd, parseUtcDate } from '../_shared/api/nullable';
import { ConfirmDeleteDialog } from '../_shared/components/ConfirmDeleteDialog';
import { DocumentPreviewDialog } from '../_shared/components/DocumentPreviewDialog';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { EntityTable } from '../_shared/components/EntityTable';
import { ProgramDocumentPicker } from '../_shared/components/ProgramDocumentPicker';
import { CONTRACT_DOCUMENT_CATEGORY, DOCUMENT_CATEGORY_OPTIONS } from '../_shared/constants/documentCategories';
import { useProgram } from '../Programs/hooks';
import { useCreateProgramDocument, useDeleteProgramDocument, useProgramDocuments } from './hooks';
function TitleCell({ row, onOpen }) {
    if (!row.fileId) return /*#__PURE__*/ _jsx(_Fragment, {
        children: row.title
    });
    return /*#__PURE__*/ _jsx(SpecraButton, {
        variant: "link",
        size: "sm",
        className: "h-auto p-0",
        onClick: (e)=>{
            e.stopPropagation();
            onOpen(row);
        },
        "data-testid": Testids.deliveryDocs.documentOpen,
        children: row.title
    });
}
function buildColumns(onOpen) {
    return [
        {
            key: 'title',
            header: 'Title',
            render: (row)=>/*#__PURE__*/ _jsx(TitleCell, {
                    row: row,
                    onOpen: onOpen
                })
        },
        {
            key: 'category',
            header: 'Category'
        },
        {
            // doc_date is date-only and stored at UTC midnight, so it renders in UTC
            // (formatUtcYmd) rather than the reader's zone — read locally, a
            // 2026-05-13 00:00+00 document shows as 2026-05-12 for every viewer west
            // of UTC. The Lineage tab reads this same field and formats it the same
            // way; the two tabs must not disagree about a document's calendar day.
            // The shared local formatter is left alone — other surfaces hold true
            // local instants and want it.
            key: 'docDate',
            header: 'Doc date',
            render: (row)=>row.docDate ? formatUtcYmd(row.docDate) ?? '—' : '—'
        },
        {
            key: 'fileName',
            header: 'File'
        },
        {
            key: 'uploadedBy',
            header: 'Uploaded by'
        }
    ];
}
const fields = [
    {
        kind: 'text',
        name: 'title',
        label: 'Title',
        required: true,
        maxLength: 300
    },
    {
        kind: 'textarea',
        name: 'description',
        label: 'Description',
        rows: 2
    },
    {
        kind: 'date',
        name: 'docDate',
        label: 'Document date',
        required: true
    },
    {
        kind: 'select',
        name: 'category',
        label: 'Category',
        required: true,
        options: DOCUMENT_CATEGORY_OPTIONS
    }
];
export function DocumentsTab({ programId }) {
    const { canEdit } = useProgramContext();
    const { data, isLoading } = useProgramDocuments(programId);
    const { data: programData, isLoading: isProgramLoading } = useProgram(programId);
    const zouProjectId = programData?.program?.zouProjectId ?? '';
    const create = useCreateProgramDocument();
    const remove = useDeleteProgramDocument();
    const upload = useUploadArtifacts();
    const uploadProjectFiles = useUploadProjectFiles();
    const [formOpen, setFormOpen] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [fileId, setFileId] = useState('');
    // A locally staged file, uploaded only on submit — cancelling the dialog
    // orphans nothing, and a failed upload records nothing. Mutually exclusive
    // with the picker: one document references one file.
    const [stagedFiles, setStagedFiles] = useState([]);
    // Which fileId, if any, was produced by the Contract project-file upload
    // lane. stagedFiles is cleared right after upload (see below), so a retry
    // after a failed create can't tell "new contract upload" from "legacy
    // upload" by stagedFiles alone — this survives across that retry.
    const [contractLaneFileId, setContractLaneFileId] = useState(null);
    const [previewing, setPreviewing] = useState(null);
    const columns = useMemo(()=>buildColumns(setPreviewing), []);
    const handleSubmit = async (values)=>{
        // A Contract submit while the program lookup is still pending must not
        // silently read an empty zouProjectId as "no linked project" — that's
        // indistinguishable from the real fallback case below and would drop the
        // upload into the legacy record-only path nondeterministically. Block
        // and let the user retry once the lookup resolves.
        if (values.category === CONTRACT_DOCUMENT_CATEGORY && isProgramLoading) {
            throw new Error('Program details are still loading — wait a moment and try again to upload this contract.');
        }
        // Route into the converging re-extraction lane (zou ProcessBatch ->
        // contract pipeline -> ryugu SyncFromExtraction -> pangaea
        // stageReExtractionChanges) only for a genuinely NEW upload of a
        // Contract-category document against a program backed by a zou contract
        // project. A document picked from ProgramDocumentPicker is already
        // recorded — there's no new file to feed the pipeline, so picking always
        // takes the legacy record-only path below regardless of category. A
        // Contract-category upload with no zouProjectId (a hand-created program
        // with no contract project behind it) also falls back to the legacy
        // path: there is nothing to extract against, and the user must not be
        // blocked from recording the document.
        const isNewContractUpload = values.category === CONTRACT_DOCUMENT_CATEGORY && !!zouProjectId && stagedFiles.length > 0;
        let resolvedFileId = fileId;
        if (stagedFiles.length > 0) {
            if (isNewContractUpload) {
                const result = await uploadProjectFiles.mutateAsync({
                    projectId: zouProjectId,
                    files: stagedFiles
                });
                if (result.failed.length > 0) {
                    throw new Error(result.failed.map((f)=>`${f.name}: ${f.error}`).join('; '));
                }
                // attachArtifactsToProject creates the project Attachment keyed by the
                // artifact id itself (tesoro mode: attachment_id == artifact_id), so
                // the same id returned here doubles as the document's fileId below.
                resolvedFileId = result.files[0]?.attachmentId ?? '';
            } else {
                const result = await upload.mutateAsync({
                    files: stagedFiles
                });
                if (result.failed.length > 0) {
                    throw new Error(result.failed.map((f)=>`${f.name}: ${f.error}`).join('; '));
                }
                resolvedFileId = result.files[0]?.artifactId ?? '';
            }
            if (!resolvedFileId) {
                throw new Error('Upload did not return a file id — the document was not recorded');
            }
            // The artifact is uploaded now. Move it out of staging so a retry after
            // a failed create reuses it instead of uploading a second copy.
            setStagedFiles([]);
            setFileId(resolvedFileId);
            // Remember which lane produced resolvedFileId beyond this call — if
            // create fails below, the retry's stagedFiles will already be empty
            // and can no longer tell the lane apart on its own.
            setContractLaneFileId(isNewContractUpload ? resolvedFileId : null);
        }
        if (!resolvedFileId) {
            throw new Error('Pick a file or upload one before recording the document');
        }
        const isContractReExtraction = values.category === CONTRACT_DOCUMENT_CATEGORY && !!zouProjectId && (isNewContractUpload || contractLaneFileId === resolvedFileId);
        await create.mutateAsync({
            programId,
            title: values.title,
            description: values.description ?? '',
            // doc_date is date-only: the column holds a calendar day, and every
            // other writer (reverie's contract sync) anchors it at UTC midnight.
            // Parsing the picked day as LOCAL midnight would store the previous
            // day's instant for any user east of UTC (Tokyo: 2026-05-13 ->
            // 2026-05-12T15:00Z), and the UTC read above would then render the day
            // before the one that was typed. parseUtcDate keeps write and read on
            // the same anchor so the day round-trips in every zone. The shared
            // local parser is left alone — other forms hold true local instants.
            docDate: parseUtcDate(values.docDate),
            fileId: resolvedFileId,
            // The server resolves the display filename from the artifact itself
            // and ignores this field — see ryugu's CreateProgramDocument.
            fileName: '',
            category: values.category,
            // The server derives this from the authenticated caller and ignores
            // this field — see ryugu's CreateProgramDocument.
            uploadedBy: ''
        });
        setFormOpen(false);
        setFileId('');
        setStagedFiles([]);
        setContractLaneFileId(null);
        if (isContractReExtraction) {
            // The document is already recorded above, so a processBatch failure here
            // must not roll it back or read as an overall failure — surface the
            // extraction failure distinctly so the user knows the file landed but
            // re-extraction did not start.
            try {
                const batch = await proposalProjectApiConnect.processBatch(zouProjectId, [
                    resolvedFileId
                ], {
                    documentKind: 'base'
                });
                useProcessingJobsStore.getState().registerJob({
                    batchId: batch.batchId,
                    zouProjectId,
                    kind: 'contract-docs',
                    label: 'Re-extracting contract',
                    link: `/delivery/programs/${programId}/docs`
                });
                toast.success('Contract recorded — re-extracting now (watch the processing tray). Proposed changes will appear on the Modifications tab shortly.');
            } catch (err) {
                toast.error(`Document recorded, but re-extraction failed to start: ${err instanceof Error ? err.message : 'unknown error'}`);
            }
        }
    };
    const handleDelete = async ()=>{
        if (!deleting) return;
        await remove.mutateAsync(deleting.id);
        setDeleting(null);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: ()=>{
                        setFileId('');
                        setStagedFiles([]);
                        setContractLaneFileId(null);
                        setFormOpen(true);
                    },
                    disabled: !canEdit,
                    "data-testid": Testids.deliveryDocs.documentCreate,
                    children: "Record Document"
                })
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: data?.items ?? [],
                columns: columns,
                testid: Testids.deliveryDocs.documentsTable,
                rowTestid: Testids.deliveryDocs.documentRow,
                rowKey: (d)=>d.id,
                isLoading: isLoading,
                hideRowActions: !canEdit,
                rowActions: (row)=>/*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        className: "text-danger hover:bg-danger-muted hover:text-danger",
                        onClick: ()=>setDeleting(row),
                        "data-testid": Testids.deliveryDocs.documentDelete,
                        children: "Delete"
                    })
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: "Record Program Document",
                fields: fields,
                defaultValues: {
                    title: '',
                    description: '',
                    docDate: '',
                    category: 'Other'
                },
                extraContent: /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-2",
                    children: [
                        /*#__PURE__*/ _jsx(ProgramDocumentPicker, {
                            id: "documentFileId",
                            label: "File",
                            helpText: "Pick a file from the documents already recorded on this program.",
                            value: fileId,
                            onChange: (id)=>{
                                setFileId(id);
                                if (id) setStagedFiles([]);
                            },
                            programId: programId
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-xs text-muted-foreground",
                            children: "…or upload a new file:"
                        }),
                        /*#__PURE__*/ _jsx(FileUploader, {
                            files: stagedFiles,
                            onChange: (f)=>{
                                const one = f.slice(-1);
                                setStagedFiles(one);
                                if (one.length > 0) setFileId('');
                            },
                            disabled: create.isPending || upload.isPending || uploadProjectFiles.isPending,
                            inputTestid: Testids.deliveryDocs.documentUploadInput
                        })
                    ]
                }),
                onSubmit: handleSubmit,
                isSubmitting: create.isPending || upload.isPending || uploadProjectFiles.isPending,
                submitLabel: "Record"
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Delete Document",
                description: deleting ? `Delete "${deleting.title}"? This cannot be undone.` : undefined,
                onConfirm: handleDelete,
                isPending: remove.isPending
            }),
            previewing && /*#__PURE__*/ _jsx(DocumentPreviewDialog, {
                artifactId: previewing.fileId,
                title: previewing.title,
                open: !!previewing,
                onOpenChange: (open)=>!open && setPreviewing(null)
            })
        ]
    });
}
